import { Navigate, Link, useParams } from "@/lib/router-compat";
import { motion } from "framer-motion";
import SEOHead from "@/components/SEOHead";
import { ArrowRight, Rocket, Globe, TrendingUp } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import Navbar from "@/components/Navbar";
import ScrollToTop from "@/components/ScrollToTop";
import Footer from "@/components/Footer";
import ContactCTA from "@/components/ContactCTA";
import BreadcrumbJsonLd from "@/components/BreadcrumbJsonLd";
import { useScrollAnimation } from "@/components/useScrollAnimation";
import { solutions, type SolutionData } from "@/data/solutionsData";
import { useLanguage, langPrefix } from "@/hooks/useLanguage";
import { getPillarT } from "@/i18n/about";

const SITE = "https://qbdgroup.com";

interface PillarConfig {
  slug: string;
  title: string;
  tagline: string;
  description: string;
  shortDesc: string;
  icon: LucideIcon;
  color: string;
  solutionSlugs: string[];
}

const pillars: PillarConfig[] = [
  {
    slug: "product-development",
    title: "Product Development",
    tagline: "From idea to patient",
    description:
      "We guide your product from early strategy to post-market. By integrating deep regulatory, clinical, and quality expertise, we accelerate development, de-risk submissions, and maximize long-term asset value. This helps you hit your milestones and stay compliant throughout the lifecycle.",
    shortDesc: "Supporting innovators in MedTech and Biotech from idea to patient.",
    icon: Rocket,
    color: "#1B4F8A",
    solutionSlugs: [
      "emerging-biotech",
      "startups",
      "medical-device-software",
      "precision-dx",
      "combination-products",
    ],
  },
  {
    slug: "gateway-to-europe",
    title: "Gateway to Europe",
    tagline: "Access Europe with confidence",
    description:
      "We make entering and succeeding in Europe seamless. With deep regulatory expertise and local presence, we deliver end-to-end support — from strategy and submissions through importation and post-market compliance. As a result, you get a smooth, compliant path to European patients.",
    shortDesc: "Enabling non-EU companies to access and thrive in the European market.",
    icon: Globe,
    color: "#0E7C6B",
    solutionSlugs: [
      "landing-in-europe",
      "your-medtech-gateway-to-europe",
    ],
  },
  {
    slug: "operational-excellence",
    title: "Operational Excellence",
    tagline: "Strengthen your operations",
    description:
      "We embed quality, compliance, and efficiency into your operations. From validation and quality systems to digital transformation and lifecycle management, we deliver fit-for-purpose solutions that strengthen your operations while adhering to the highest industry standards.",
    shortDesc: "Strengthening manufacturing quality and lifecycle activities.",
    icon: TrendingUp,
    color: "#6B4C9A",
    solutionSlugs: [
      "manufacturing-quality-compliance",
      "life-cycle-management",
    ],
  },
];

/* ── Merged solution config ── */
interface MergedSolution {
  slug: string;
  title: string;
  description: string;
  icon: LucideIcon;
  subLinks: { label: string; path: string }[];
}

const mergedSolutions: MergedSolution[] = [
  {
    slug: "life-cycle-management",
    title: "Life Cycle Management",
    description: "Keeping products safe, compliant & competitive post-approval",
    icon: TrendingUp,
    subLinks: [
      { label: "Pharma & Biotech", path: "/en/industries/pharma-biotech/life-cycle-management" },
      { label: "MD & IVD", path: "/en/industries/medical-devices/life-cycle-management" },
    ],
  },
];

const SolutionCard = ({ solution, color, index }: { solution: SolutionData; color: string; index: number }) => {
  const { ref, isInView } = useScrollAnimation();
  const Icon = solution.icon;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.4, delay: index * 0.08 }}
    >
      <Link
        to={solution.path}
        className="group relative flex flex-col sm:flex-row items-start gap-5 p-6 sm:p-8 rounded-2xl border border-border bg-card hover:border-border/80 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden"
      >
        <div
          className="flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
          style={{ backgroundColor: `${color}10` }}
        >
          <Icon size={28} strokeWidth={1.4} style={{ color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[11px] font-medium px-2.5 py-0.5 rounded-full border" style={{ borderColor: `${color}30`, color }}>
              {solution.category === "pharma" ? "Pharma & Biotech" : "MD & IVD"}
            </span>
          </div>
          <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors mt-2">
            {solution.cardTitle || solution.title}
          </h3>
          <p className="text-sm text-muted-foreground leading-relaxed mt-2 line-clamp-3">
            {solution.description.split("\n\n")[0].replace(/\*\*/g, "")}
          </p>
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary mt-4 group-hover:gap-2.5 transition-all">
            Explore solution <ArrowRight size={14} />
          </span>
        </div>
      </Link>
    </motion.div>
  );
};

const MergedSolutionCard = ({ merged, color, index }: { merged: MergedSolution; color: string; index: number }) => {
  const { ref, isInView } = useScrollAnimation();
  const Icon = merged.icon;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.4, delay: index * 0.08 }}
    >
      <div className="relative flex flex-col sm:flex-row items-start gap-5 p-6 sm:p-8 rounded-2xl border border-border bg-card overflow-hidden">
        <div
          className="flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${color}10` }}
        >
          <Icon size={28} strokeWidth={1.4} style={{ color }} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-foreground mt-2">{merged.title}</h3>
          <p className="text-sm text-muted-foreground leading-relaxed mt-2">{merged.description}</p>
          <div className="flex items-center gap-2 mt-4">
            {merged.subLinks.map((sl) => (
              <Link
                key={sl.path}
                to={sl.path}
                className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80 border border-border rounded-full px-3 py-1 hover:bg-muted transition-all"
              >
                {sl.label} <ArrowRight size={10} />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const SolutionPillar = () => {
  const { pillarSlug } = useParams<{ pillarSlug: string }>();
  const pillar = pillars.find((p) => p.slug === pillarSlug);
  const lang = useLanguage();
  const pt = getPillarT(lang);
  const prefix = langPrefix(lang);

  if (!pillar) return <Navigate to={`${lang === "es" ? "/es/soluciones" : "/en/solutions"}`} replace />;

  const pillarSolutions = pillar.solutionSlugs
    .map((slug) => solutions.find((s) => s.slug === slug))
    .filter((s): s is SolutionData => !!s);
  const pillarMerged = mergedSolutions.filter((m) => pillar.solutionSlugs.includes(m.slug));
  const totalCount = pillarSolutions.length + pillarMerged.length;

  const pillarI18n = pt.pillars[pillar.slug];
  const displayTitle = pillarI18n?.title ?? pillar.title;
  const displayTagline = pillarI18n?.tagline ?? pillar.tagline;
  const displayDescription = pillarI18n?.description ?? pillar.description;
  const displayShortDesc = pillarI18n?.shortDesc ?? pillar.shortDesc;

  const { ref: heroRef, isInView: heroInView } = useScrollAnimation();

  return (
    <>
      <SEOHead
        title={`${displayTitle} — Life Sciences Solutions | QbD Group`}
        description={displayDescription.slice(0, 155)}
        canonical={`${SITE}${lang === "es" ? "/es/soluciones" : "/en/solutions"}/${pillar.slug}`}
      />
      <BreadcrumbJsonLd items={[{ name: lang === "es" ? "Soluciones" : "Solutions", path: lang === "es" ? "/es/soluciones" : "/en/solutions" }, { name: displayTitle }]} />

      <ScrollToTop />
      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative bg-primary text-primary-foreground pt-44 pb-20 sm:pb-28 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/95 to-accent/20" />
          <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full border border-primary-foreground/5" />
          <div className="absolute -right-10 -top-10 w-60 h-60 rounded-full border border-primary-foreground/5" />

          <div className="relative z-10 container mx-auto px-6 lg:px-10 max-w-4xl text-center">
            <motion.div ref={heroRef} initial={{ opacity: 0, y: 30 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }}>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
                {displayTitle}
              </h1>
              <p className="text-lg text-primary-foreground/70 max-w-2xl mx-auto leading-relaxed">
                {displayTagline}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Description */}
        <section className="py-16 sm:py-24">
          <div className="container mx-auto px-6 lg:px-10 max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <p className="text-lg sm:text-xl text-foreground leading-relaxed">
                {displayDescription}
              </p>
              <p className="mt-6 text-base font-semibold text-muted-foreground italic">
                {pt.inShort} {displayShortDesc.toLowerCase()}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Solutions grid */}
        <section className="pb-20 sm:pb-28">
          <div className="container mx-auto px-6 lg:px-10">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center justify-center gap-3 mb-3">
                <div className="w-1 h-6 rounded-full" style={{ backgroundColor: pillar.color }} />
                <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
                  Our {pillar.title} solutions
                </h2>
              </div>
              <p className="text-muted-foreground mt-2">
                {totalCount} tailored solution{totalCount !== 1 ? "s" : ""} for your needs
              </p>
            </motion.div>

            <div className="grid gap-5 max-w-4xl mx-auto">
              {pillarSolutions.map((s, i) => (
                <SolutionCard key={s.slug} solution={s} color={pillar.color} index={i} />
              ))}
              {pillarMerged.map((m, i) => (
                <MergedSolutionCard key={m.slug} merged={m} color={pillar.color} index={pillarSolutions.length + i} />
              ))}
            </div>
          </div>
        </section>

        {/* Cross-links to other pillars */}
        <section className="py-16 sm:py-20 bg-muted/30">
          <div className="container mx-auto px-6 lg:px-10">
            <motion.div
              className="text-center mb-10"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground">{pt.exploreOther}</h2>
            </motion.div>

            <div className="grid sm:grid-cols-2 gap-5 max-w-3xl mx-auto">
              {pillars
                .filter((p) => p.slug !== pillar.slug)
                .map((p, i) => {
                  const OtherIcon = p.icon;
                  return (
                    <motion.div
                      key={p.slug}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: i * 0.1 }}
                    >
                      <Link
                        to={`${lang === "es" ? "/es/soluciones" : "/en/solutions"}/${p.slug}`}
                        className="group flex items-center gap-4 p-6 rounded-2xl border border-border bg-card hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                      >
                        <div
                          className="w-12 h-12 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: `${p.color}10` }}
                        >
                          <OtherIcon size={24} strokeWidth={1.4} style={{ color: p.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base font-semibold text-foreground group-hover:text-primary transition-colors">
                            {pt.pillars[p.slug]?.title ?? p.title}
                          </h3>
                          <p className="text-sm text-muted-foreground mt-0.5">{pt.pillars[p.slug]?.shortDesc ?? p.shortDesc}</p>
                        </div>
                        <ArrowRight size={16} className="text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all shrink-0" />
                      </Link>
                    </motion.div>
                  );
                })}
            </div>
          </div>
        </section>

        <ContactCTA />
      </main>

      <Footer />
    </>
  );
};

export default SolutionPillar;
